import React, { useEffect, useRef, useState } from "react";
import RecipeHeader from "./components/RecipeHeader";
import RecipeInstructions from "./components/components/RecipeInstructions";
import Comment from "./components/Comment";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { populateRecipeDetails } from "../../redux/recipe/recipesThunk";
import { selectLoading, setLoading } from "../../redux/loader/loaderSlice";
import Loader from "../../components/Loader";
import { selectCurrentUser, selectUser } from "../../redux/user/userSlice";
import SignInModal from "./components/components/SignInModal";
import PrintDetails from "./components/PrintDetails";
import { selectCurrentRecipe } from "../../redux/recipe/recipesSlice";
import { useReactToPrint } from "react-to-print";
import VideoPlayer from "./components/VideoPlayer";

const NUTRITION_INFO_PREFERENCE_KEY = "wwl_show_nutrition_info";

const getNutritionInfoPreference = () => {
  return localStorage.getItem(NUTRITION_INFO_PREFERENCE_KEY) === "true";
};

const roundNutritionValue = (value, decimals = 1) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number(numericValue.toFixed(decimals));
};

const calculateAdjustedPerServingNutritionInfo = (
  nutritionInfo,
  servingsCount
) => {
  const totalNutritionInfo = nutritionInfo?.total;
  const originalPerServingNutritionInfo = nutritionInfo?.perServing;
  const numericServingsCount = Number(servingsCount);

  if (
    totalNutritionInfo &&
    typeof totalNutritionInfo === "object" &&
    Number.isFinite(numericServingsCount) &&
    numericServingsCount > 0
  ) {
    return {
      calories: Math.round(
        Number(totalNutritionInfo.calories || 0) / numericServingsCount
      ),
      protein: roundNutritionValue(
        Number(totalNutritionInfo.protein || 0) / numericServingsCount
      ),
      carbs: roundNutritionValue(
        Number(totalNutritionInfo.carbs || 0) / numericServingsCount
      ),
      fat: roundNutritionValue(
        Number(totalNutritionInfo.fat || 0) / numericServingsCount
      ),
    };
  }

  return originalPerServingNutritionInfo;
};

const formatNutritionValue = (value, suffix = "") => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return suffix ? `0${suffix}` : "0";
  }

  return `${numericValue}${suffix}`;
};

const RecipeDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);

  const user = useSelector(selectCurrentUser);
  const currentRecipe = useSelector(selectCurrentRecipe);

  // ✅ User defaults (DO NOT mutate from this page)
  const { primaryDiet, isDairyFree, isGlutenFree, currentUser } =
    useSelector(selectUser);

  const [loadingTab, setLoadingTab] = useState(false);
  const [servingsCount, setServingsCount] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showNutritionInfoPreference, setShowNutritionInfoPreference] =
    useState(getNutritionInfoPreference);

  const handleNullUser = () => setShowSignInModal(true);

  // ✅ prevents older/slower toggles from overwriting newer ones
  const lastReqIdRef = useRef(0);

  // ==========================
  // ✅ NEW: Recipe "view" diet state (local-only)
  // ==========================
  const [viewPrimaryDiet, setViewPrimaryDiet] = useState(primaryDiet);
  const [viewIsDairyFree, setViewIsDairyFree] = useState(isDairyFree);
  const [viewIsGlutenFree, setViewIsGlutenFree] = useState(isGlutenFree);

  useEffect(() => {
    const handleNutritionInfoPreferenceChange = () => {
      setShowNutritionInfoPreference(getNutritionInfoPreference());
    };

    window.addEventListener(
      "nutritionInfoPreferenceChanged",
      handleNutritionInfoPreferenceChange
    );

    window.addEventListener("storage", handleNutritionInfoPreferenceChange);

    return () => {
      window.removeEventListener(
        "nutritionInfoPreferenceChanged",
        handleNutritionInfoPreferenceChange
      );

      window.removeEventListener("storage", handleNutritionInfoPreferenceChange);
    };
  }, []);

  // ✅ Reset view diet when recipe changes OR when user defaults change
  useEffect(() => {
    setViewPrimaryDiet(primaryDiet);
    setViewIsDairyFree(isDairyFree);
    setViewIsGlutenFree(isGlutenFree);
  }, [id, primaryDiet, isDairyFree, isGlutenFree]);

  // Keep servingsCount in sync
  useEffect(() => {
    setServingsCount(currentRecipe?.servings);
  }, [currentRecipe]);

  // ✅ Initial load
  useEffect(() => {
    async function fetchInitial() {
      dispatch(setLoading(true));
      await dispatch(
        populateRecipeDetails({
          id,
          userId: user?._id,
          primaryDiet: viewPrimaryDiet,
          isGlutenFree: viewIsGlutenFree,
          isDairyFree: viewIsDairyFree,
        })
      );
      dispatch(setLoading(false));
    }

    if (!id) return;
    fetchInitial().catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  // ✅ When VIEW switches change: refetch recipe details (NO preference persistence)
  useEffect(() => {
    if (!id) return;

    // If app treats null as "logged out"
    if (currentUser === null) {
      handleNullUser();
      return;
    }

    // While currentUser is still loading, do nothing
    if (!currentUser) return;

    const reqId = ++lastReqIdRef.current;

    const run = async () => {
      setLoadingTab(true);

      await dispatch(
        populateRecipeDetails({
          id,
          userId: user?._id,
          primaryDiet: viewPrimaryDiet,
          isGlutenFree: viewIsGlutenFree,
          isDairyFree: viewIsDairyFree,
        })
      );
    };

    run()
      .catch((e) => console.error(e))
      .finally(() => {
        if (reqId === lastReqIdRef.current) setLoadingTab(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, viewPrimaryDiet, viewIsDairyFree, viewIsGlutenFree, currentUser]);

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef, // ✅ new API
  });

  const {
    prep_time,
    cook_time,
    servings,
    default_ingredients,
    default_instructions,
    description,
    nutritionInfo,
  } = currentRecipe || {};

  if (loading) return <Loader />;

  // ✅ Render from the source that refresh uses
  const ingredientsToShow = default_ingredients;
  const instructionsToShow = default_instructions;

  // ✅ If RecipeInstructions caches internally, remount it when view changes
  const recipeViewKey = `${id}-${viewPrimaryDiet}-${viewIsDairyFree}-${viewIsGlutenFree}`;

  const adjustedPerServingNutritionInfo =
    calculateAdjustedPerServingNutritionInfo(nutritionInfo, servingsCount);

  const hasNutritionInfo =
    showNutritionInfoPreference &&
    adjustedPerServingNutritionInfo &&
    typeof adjustedPerServingNutritionInfo === "object" &&
    Object.keys(adjustedPerServingNutritionInfo).length > 0;

  return (
    <>
      <div className="bg-wwlWhite mx-6 md:p-8 p-6 border border-wwlBorderColor rounded-xl md:rounded-2xl mt-10 print-hide">
        <RecipeHeader
          recipe={currentRecipe}
          handleNullUser={handleNullUser}
          currentUser={currentUser}
          handlePrint={handlePrint}
          // ✅ Pass view diet + setters so RecipeHeader toggles do NOT change defaults
          dietView={{
            viewPrimaryDiet,
            viewIsDairyFree,
            viewIsGlutenFree,
          }}
          setDietView={{
            setViewPrimaryDiet,
            setViewIsDairyFree,
            setViewIsGlutenFree,
          }}
        />

        {hasNutritionInfo && (
          <div className="max-w-4xl mx-auto mt-6 mb-2">
            <div className="mt-4 border border-wwlBorderColor rounded-xl p-4 bg-white">
              <div className="flex flex-col gap-1 mb-4">
                <h3 className="font-inter text-lg font-bold text-wwlBlack">
                  {nutritionInfo?.isEstimate
                    ? "Estimated nutrition info"
                    : "Nutrition info"}
                </h3>

                <p className="font-inter text-sm text-wwlGray500">
                  Per serving based on {servingsCount || servings} servings
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-wwlBorderColor p-3">
                  <p className="font-inter text-xl font-bold text-wwlBlack">
                    {formatNutritionValue(
                      adjustedPerServingNutritionInfo?.calories
                    )}
                  </p>
                  <p className="font-inter text-xs text-wwlGray500">Calories</p>
                </div>

                <div className="rounded-lg border border-wwlBorderColor p-3">
                  <p className="font-inter text-xl font-bold text-wwlBlack">
                    {formatNutritionValue(
                      adjustedPerServingNutritionInfo?.protein,
                      "g"
                    )}
                  </p>
                  <p className="font-inter text-xs text-wwlGray500">Protein</p>
                </div>

                <div className="rounded-lg border border-wwlBorderColor p-3">
                  <p className="font-inter text-xl font-bold text-wwlBlack">
                    {formatNutritionValue(
                      adjustedPerServingNutritionInfo?.carbs,
                      "g"
                    )}
                  </p>
                  <p className="font-inter text-xs text-wwlGray500">
                    Carbohydrates
                  </p>
                </div>

                <div className="rounded-lg border border-wwlBorderColor p-3">
                  <p className="font-inter text-xl font-bold text-wwlBlack">
                    {formatNutritionValue(
                      adjustedPerServingNutritionInfo?.fat,
                      "g"
                    )}
                  </p>
                  <p className="font-inter text-xs text-wwlGray500">Fat</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <RecipeInstructions
          key={recipeViewKey}
          prepTime={prep_time}
          cookTime={cook_time}
          servings={servings}
          ingredients={ingredientsToShow}
          instructions={instructionsToShow}
          dairyFree={viewIsDairyFree}
          glutenFree={viewIsGlutenFree}
          loadingTab={loadingTab}
          servingsCount={servingsCount}
          setServingsCount={setServingsCount}
        />

        <Comment description={description} />

        <div className="mx-auto">
          <VideoPlayer videoUrl={currentRecipe?.videoUrl} />
        </div>

        <p className="font-base max-w-xl mx-auto font-inter text-xs text-wwlGray500 mt-0 text-center sm:text-xs sm:mt-2">
          Did you like this recipe? Don&apos;t forget to rate it and share a
          photo of your meal prep masterpiece in our{" "}
          <a
            href="https://www.facebook.com/groups/workweekpreppers"
            className="font-bold text-wwlOrange"
          >
            Facebook Group
          </a>{" "}
          !
        </p>

        <SignInModal open={showSignInModal} setOpen={setShowSignInModal} />
      </div>

      <div>
        <PrintDetails
          key={`print-${recipeViewKey}`}
          prepTime={prep_time}
          cookTime={cook_time}
          servings={servings}
          title={currentRecipe?.title}
          ingredients={ingredientsToShow}
          instructions={instructionsToShow}
          dairyFree={viewIsDairyFree}
          glutenFree={viewIsGlutenFree}
          servingsCount={servingsCount}
          ref={componentRef}
        />
      </div>
    </>
  );
};

export default RecipeDetail;